import { Link } from 'react-router-dom'
import { Modal, Button, Badge } from '@/components/ui'
import { SectionLoader } from '@/components/common/SectionLoader'
import { usePermissions } from '@/hooks/usePermissions'
import { useApproveBonus, useReverseBonus } from '@/features/bonuses/hooks/useBonuses'
import { formatCurrency, formatDateTime } from '@/lib/formatters'
import {
  bonusStatusBadgeVariant,
  bonusStatusLabel,
  bonusTypeLabel,
  formatBonusPeriod,
} from './bonusUtils'
import type { BonusEntry } from '@/features/bonuses/types/bonus.types'

interface BonusDetailModalProps {
  companyId: string
  entry: BonusEntry | null
  onClose: () => void
}

function DetailRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-4 py-2 text-sm">
      <span className="shrink-0 font-medium text-gray-600 dark:text-gray-400">{label}</span>
      <span className="text-right text-gray-900 dark:text-gray-100">{value}</span>
    </div>
  )
}

export function BonusDetailModal({
  companyId,
  entry,
  onClose,
}: BonusDetailModalProps): React.ReactElement | null {
  const { can } = usePermissions()
  const approveMutation = useApproveBonus()
  const reverseMutation = useReverseBonus()

  if (!entry) return null

  const canApprove = can('bonuses', 'approve')
  const canReverse = can('bonuses', 'reverse')
  const canReadAudit = can('audit', 'read')

  const isPending = approveMutation.isPending || reverseMutation.isPending

  const handleApprove = () => {
    approveMutation.mutate(
      { companyId, id: entry._id },
      { onSuccess: onClose },
    )
  }

  const handleReverse = () => {
    reverseMutation.mutate(
      { companyId, id: entry._id },
      { onSuccess: onClose },
    )
  }

  const formattedAmount = formatCurrency(entry.amount, entry.currency)

  const showApprove = canApprove && entry.status === 'pending_approval'
  const showReverse = canReverse && entry.status !== 'reversed'

  return (
    <Modal
      open={!!entry}
      onClose={onClose}
      title="Bonus details"
      footer={
        showApprove || showReverse ? (
          <div className="flex flex-wrap gap-2">
            {showApprove && (
              <Button
                type="button"
                variant="primary"
                onClick={handleApprove}
                loading={isPending}
              >
                {approveMutation.isPending ? 'Approving…' : 'Approve'}
              </Button>
            )}
            {showReverse && (
              <Button
                type="button"
                variant="danger"
                onClick={handleReverse}
                loading={isPending}
              >
                {reverseMutation.isPending ? 'Reversing…' : 'Reverse'}
              </Button>
            )}
            <Button type="button" variant="secondary" onClick={onClose} disabled={isPending}>
              Close
            </Button>
          </div>
        ) : (
          <Button type="button" variant="secondary" onClick={onClose}>
            Close
          </Button>
        )
      }
    >
      {isPending ? (
        <SectionLoader variant="detail" label="Loading bonus" />
      ) : (
        <div className="divide-y divide-gray-100 dark:divide-gray-700">
          <DetailRow
            label="Status"
            value={
              <Badge variant={bonusStatusBadgeVariant(entry.status)}>
                {bonusStatusLabel(entry.status)}
              </Badge>
            }
          />
          <DetailRow label="Type" value={bonusTypeLabel(entry.bonusType)} />
          <DetailRow label="Period" value={formatBonusPeriod(entry.year, entry.month)} />
          <DetailRow label="Amount" value={formattedAmount} />
          {entry.reason && <DetailRow label="Reason" value={entry.reason} />}
          {entry.createdBy && (
            <DetailRow
              label="Issued by"
              value={<span className="font-mono text-xs">{entry.createdBy}</span>}
            />
          )}
          <DetailRow label="Issued at" value={formatDateTime(entry.createdAt)} />
          {entry.approvedBy && (
            <DetailRow
              label="Approved by"
              value={<span className="font-mono text-xs">{entry.approvedBy}</span>}
            />
          )}
          {entry.approvedAt && (
            <DetailRow label="Approved at" value={formatDateTime(entry.approvedAt)} />
          )}
          {canReadAudit && (
            <div className="pt-2">
              <Link
                to={`/audit-logs?module=bonuses&entityId=${entry._id}&entityType=bonus_entry`}
                className="text-sm text-primary underline underline-offset-2 hover:opacity-80"
                onClick={onClose}
              >
                View audit history
              </Link>
            </div>
          )}
        </div>
      )}
    </Modal>
  )
}

BonusDetailModal.displayName = 'BonusDetailModal'
